import { AccessEnum, PermissionDto, RoleDto } from '@/services/api'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import {
  DataGridPro,
  GridColDef,
  GridExpandMoreIcon,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro'
import { findAllPermissions } from '@state/thunks/permissionsThunk'
import { deleteRoles, findAllRoles, updateRole } from '@state/thunks/roleThunk'
import { findAll as findAllSites } from '@state/thunks/siteThunk'
import { useEffect, useMemo, useState } from 'react'
import { CustomColumnMenu } from './CustomColumnMenu'
import RoleForm from './RoleForm'
import { enqueueSnackbar } from 'notistack'
import { useIntl } from 'react-intl'

const Permissions = () => {
  const intl = useIntl()
  const [openForm, setOpenForm] = useState(false)
  const [selectedSiteId, setSelectedSiteId] = useState<number | ''>('')
  const [editingRole, setEditingRole] = useState<RoleDto | null>(null)

  const dispatch = useAppDispatch()
  const { datas: sites } = useAppSelector((state) => state.site.sites)
  const { roles } = useAppSelector((state) => state.role)
  const { permissions } = useAppSelector((state) => state.permissions)

  useEffect(() => {
    dispatch(findAllSites())
      .unwrap()
      .then((sites) => {
        setSelectedSiteId(sites[0]?.id || '')
      })
    dispatch(findAllRoles()).unwrap()
    dispatch(findAllPermissions()).unwrap()
  }, [])

  const filteredRoles = useMemo(() => {
    if (!selectedSiteId) {
      return roles
    }

    return roles.filter((role) => role.siteId === selectedSiteId)
  }, [roles, selectedSiteId])

  const groupedPermissions = useMemo(() => {
    return permissions.reduce((acc, curr) => {
      const { category } = curr
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push(curr)
      return acc
    }, {} as Record<string, PermissionDto[]>)
  }, [permissions])

  // Handler for toggling a permission on an existing Role
  const handleTogglePermission = async (
    roleId: number,
    permissionStr: string,
    currentStatus: boolean,
  ) => {
    const targetRole = roles.find((r) => r.id === roleId)
    if (!targetRole) return

    const currentPermissionStrings = (targetRole.permissions || []).map((p) =>
      typeof p === 'string' ? p : p.permission,
    )

    let updatedPermissions: string[]

    if (currentStatus) {
      updatedPermissions = currentPermissionStrings.filter(
        (p) => p !== permissionStr,
      )
    } else {
      updatedPermissions = [...currentPermissionStrings, permissionStr]
    }

    try {
      await dispatch(
        updateRole({
          id: roleId,
          body: {
            permissions: updatedPermissions as AccessEnum[],
          },
        }),
      ).unwrap()

      await dispatch(findAllRoles()).unwrap()
      enqueueSnackbar('Role permissions updated successfully', {
        variant: 'success',
      })
    } catch (error) {
      console.error('Failed to update role permissions:', error)
      enqueueSnackbar('Failed to update role permissions', {
        variant: 'error',
      })
    }
  }

  // Helper to check if a role has a specific permission
  const checkRoleHasPermission = (
    role: RoleDto,
    permissionStr: AccessEnum,
  ): boolean => {
    if (!role.permissions) return false
    return role.permissions.some(
      (p) => (typeof p === 'string' ? p : p.permission) === permissionStr,
    )
  }

  const handleDeleteRole = async (roleId: number) => {
    try {
      await dispatch(deleteRoles({ ids: [roleId] })).unwrap()

      enqueueSnackbar('Role deleted successfully', { variant: 'success' })
    } catch (error) {
      console.error('Failed to delete role:', error)
      enqueueSnackbar('Failed to delete role', { variant: 'error' })
    }
  }

  const handleEditRole = (roleId: number) => {
    const role = roles.find((r) => r.id === roleId)

    if (role) {
      setEditingRole(role)
      setOpenForm(true)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        px: 2,
        pb: 6,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">
          {intl.formatMessage({ id: 'roles-and-permissions' })}
        </Typography>
        <Box gap={2} display="flex">
          <FormControl sx={{ width: '300px', alignSelf: 'end' }}>
            <InputLabel id="site-filter-label" size="small">
              {intl.formatMessage({ id: 'select-site' })}
            </InputLabel>
            <Select
              labelId="site-filter-label"
              label={intl.formatMessage({ id: 'select-site' })}
              className="bg-white w-full"
              size="small"
              value={selectedSiteId}
              onChange={(e) => setSelectedSiteId(Number(e.target.value))}
            >
              {sites?.map((site) => (
                <MenuItem key={site.id} value={site.id}>
                  {site.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => {
              setEditingRole(null)
              setOpenForm(true)
            }}
          >
            {intl.formatMessage({ id: 'add-new-role' })}
          </Button>
        </Box>
      </Stack>

      <Box display="flex" flexDirection="column" gap={2}>
        {selectedSiteId === '' && (
          <Typography
            color="text.secondary"
            align="center"
            py={6}
            fontSize={20}
          >
            {intl.formatMessage({ id: 'select-site-to-view-and-edit-roles' })}
          </Typography>
        )}

        {selectedSiteId !== '' &&
          Object.entries(groupedPermissions).map(([category, perms], i) => {
            const rows = perms.map((p) => ({
              id: p.permission,
              permissionName: p.permission,
              ...p,
            }))

            const columns: GridColDef[] = [
              {
                field: 'permissionName',
                headerName: 'Permission',
                flex: 2,
                minWidth: 200,
                renderCell: (params) => (
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {params.value.replace(/_/g, ' ')}
                  </Typography>
                ),
              },

              ...filteredRoles.map((role) => ({
                field: `role_${role.id}`,
                flex: 1,
                headerName: role.name,
                width: 150,
                sortable: false,
                renderCell: (params: GridRenderCellParams) => {
                  const permissionStr = params.row.permission
                  const isChecked = checkRoleHasPermission(role, permissionStr)

                  return (
                    <Checkbox
                      checked={isChecked}
                      size="small"
                      onChange={async () =>
                        await handleTogglePermission(
                          role.id,
                          permissionStr,
                          isChecked,
                        )
                      }
                    />
                  )
                },
              })),
            ]

            return (
              <Accordion
                key={category}
                defaultExpanded={i === 0}
                elevation={1}
                sx={{
                  padding: 1,
                  borderRadius: 1,
                  border: '1px solid',
                }}
                slotProps={{ transition: { timeout: 400 } }}
              >
                <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                  <Box
                    display="flex"
                    alignItems="center"
                    width="100%"
                    justifyContent="space-between"
                    pr={3}
                  >
                    <Typography fontWeight="bold">{category}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {perms.length} permissions
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ bgcolor: '#fff', paddingX: 1.5 }}>
                  {/* DataGrid Container */}
                  <Box sx={{ width: '100%' }}>
                    <DataGridPro
                      rows={rows}
                      columns={columns}
                      autoHeight
                      disableSelectionOnClick
                      components={{
                        ColumnMenu: CustomColumnMenu,
                      }}
                      componentsProps={{
                        columnMenu: {
                          onDeleteRole: handleDeleteRole,
                          onEditRole: handleEditRole,
                        },
                      }}
                      hideFooter
                      sx={{
                        border: 'none',
                        '& .MuiDataGrid-cell:focus': {
                          outline: 'none',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          bgcolor: '#fafafa',
                          borderBottom: '1px solid #f0f0f0',
                        },
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            )
          })}
      </Box>
      <RoleForm
        open={openForm}
        setOpen={setOpenForm}
        sites={sites}
        editingRole={editingRole}
        setEditingRole={setEditingRole}
      />
    </Box>
  )
}

export default Permissions
